import React from 'react';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { cn } from '@/utils/cn';
import type { TeamNewFormValues, NewReferenceData } from '../types/teamNew.types';

interface BasicInfoStepProps {
  form: TeamNewFormValues;
  onChange: (patch: Partial<TeamNewFormValues>) => void;
  referenceData: NewReferenceData;
}

export function BasicInfoStep({ form, onChange, referenceData }: BasicInfoStepProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Thông tin team */}
        <div className="lg:col-span-2 bg-white rounded-md border border-slate-100 p-6 space-y-6">
          <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Thông tin team</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Tên team *"
              placeholder="Nhập tên team"
              value={form.name}
              onChange={e => onChange({ name: e.target.value })}
            />
            
            <Select
              label="Phòng ban *"
              placeholder="Chọn phòng ban"
              options={referenceData.departments}
              value={form.department_id || ''}
              onValueChange={val => onChange({ department_id: val })}
            />
            
            <Select
              label="Trưởng team *"
              placeholder="Chọn trưởng team"
              options={referenceData.users}
              value={form.leader_user_id || ''}
              onValueChange={val => onChange({ leader_user_id: val })}
            />
          </div>

          <div className="space-y-1.5 relative">
            <label className="sm:text-sm text-xs text-slate-400 font-normal tracking-widest">Mô tả</label>
            <textarea
              className="w-full h-32 p-3 bg-slate-50 border border-slate-100 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all resize-none"
              placeholder="Nhập mô tả về team (nhiệm vụ, phạm vi công việc...)"
              maxLength={500}
              value={form.notes}
              onChange={e => onChange({ notes: e.target.value })}
            />
            <span className="absolute bottom-3 right-3 sm:text-sm text-xs text-slate-400 font-normal">
              {form.notes?.length || 0}/500
            </span>
          </div>
        </div>

        {/* Right Column: Cài đặt khác */}
        <div className="bg-white rounded-md border border-slate-100 p-6 space-y-6">
          <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Cài đặt khác</h3>
          
          <div className="space-y-4">
            <Select
              label="Trạng thái *"
              options={[
                { value: 'active', label: '● Hoạt động' },
                { value: 'inactive', label: '● Tạm ngưng' },
              ]}
              value={form.status_code}
              onValueChange={val => onChange({ status_code: val as any })}
              className={cn(
                form.status_code === 'active' ? "[&_select]:text-emerald-600" : "[&_select]:text-amber-600"
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Ngày thành lập *"
                type="date"
                value={form.founded_date}
                onChange={e => onChange({ founded_date: e.target.value })}
              />
                <Select
                  label="Loại team"
                  placeholder="Chọn loại team"
                  options={[
                    { value: 'baoduong', label: 'Bảo dưỡng' },
                    { value: 'thicong', label: 'Thi công' },
                    { value: 'vuonuom', label: 'Vườn ươm' },
                    { value: 'cogioi', label: 'Cơ giới' },
                    { value: 'khac', label: 'Khác' }
                  ]}
                  value={form.team_type}
                  onValueChange={val => onChange({ team_type: val })}
                />
            </div>

            <Select
              label="Khu vực phụ trách"
              placeholder="Chọn khu vực"
              options={referenceData.zones}
              value={form.zone_id || ''}
              onValueChange={val => onChange({ zone_id: val })}
            />

            <div>
              <Select
                label="Dự án phụ trách (tùy chọn)"
                placeholder="Chọn dự án"
                options={referenceData.projects}
                value={form.project_id || ''}
                onValueChange={val => onChange({ project_id: val })}
              />
              <p className="sm:text-sm text-xs text-slate-400 mt-1 font-normal italic">Bỏ trống nếu team chưa phụ trách dự án cụ thể</p>
            </div>

            <div>
              <Select
                label="Hợp đồng liên quan"
                placeholder="Chọn hợp đồng"
                options={referenceData.contracts}
                value={form.contract_id || ''}
                onValueChange={val => onChange({ contract_id: val })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Thông tin bổ sung */}
      <div className="bg-white rounded-md border border-slate-100 p-6 space-y-6">
        <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Thông tin bổ sung</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Email nhóm"
            placeholder="Nhập email nhóm (nếu có)"
            value={form.email}
            onChange={e => onChange({ email: e.target.value })}
          />
          <Input
            label="Số điện thoại nhóm"
            placeholder="Nhập số điện thoại nhóm (nếu có)"
            value={form.phone}
            onChange={e => onChange({ phone: e.target.value })}
          />
          <Input
            label="Địa chỉ"
            placeholder="Nhập địa chỉ (nếu có)"
            value={form.address}
            onChange={e => onChange({ address: e.target.value })}
          />
        </div>

        <div className="space-y-1.5 relative">
          <label className="sm:text-sm text-xs text-slate-400 font-normal tracking-widest">Ghi chú</label>
          <textarea
            className="w-full h-24 p-3 bg-slate-50 border border-slate-100 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all resize-none"
            placeholder="Nhập ghi chú (nếu có)"
            maxLength={500}
            value={form.additional_notes}
            onChange={e => onChange({ additional_notes: e.target.value })}
          />
          <span className="absolute bottom-3 right-3 sm:text-sm text-xs text-slate-400 font-normal">
            {form.additional_notes?.length || 0}/500
          </span>
        </div>
      </div>
    </div>
  );
}
